/**
 * LoadingOverlay Component
 * 
 * Full-screen loading overlay shown during async operations like:
 * - Creating Stripe Customer Portal sessions
 * - Processing payments
 * - Long-running operations
 */

import React from 'react';

interface LoadingOverlayProps {
  message?: string;
  onCancel?: () => void;
  show: boolean;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  message = 'Loading...',
  onCancel,
  show,
}) => {
  if (!show) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center p-4"
      style={{ backdropFilter: 'blur(4px)' }}
    >
      <div className="bg-white dark:bg-[#1a1d24] rounded-xl p-6 sm:p-8 max-w-sm w-full shadow-2xl">
        {/* Spinner */}
        <div className="flex justify-center mb-4 sm:mb-6">
          <div className="relative">
            {/* Outer spinning ring */}
            <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-white/10 dark:border-white/10 rounded-full"></div>
            {/* Inner spinning ring */}
            <div className="absolute top-0 left-0 w-12 h-12 sm:w-16 sm:h-16 border-4 border-transparent border-t-[#F9F7EA] rounded-full animate-spin"></div>
          </div>
        </div>

        {/* Message */}
        <p className="text-center text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-2">
          {message}
        </p>
        
        <p className="text-center text-xs sm:text-sm text-gray-500 dark:text-gray-400">
          Please wait, this may take a moment...
        </p>

        {/* Cancel button (optional) */}
        {onCancel && (
          <button
            onClick={onCancel}
            className="mt-4 sm:mt-6 w-full px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors min-h-[44px]"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

export default LoadingOverlay;

